// How a league's format plays a week. Pure. Scenarios Q1 to Q5,
// PLAN-2026-10-16-league-formats.
//
// IN THE DOMAIN for `result.ts`'s reason: the engine that settles a week and
// every client that explains it need one answer to "what is the median" and
// "does this league have opponents", or a screen will one day say a team beat
// a median the standings say it lost to.

import type { LeagueSettings } from '../rules/schemas';

/**
 * The formats whose regular season has no opponents: each team plays the
 * whole league every week (`all-play`, `points-total`, `elimination`). Their
 * weeks are `regular` matchup rows with nobody away (`isFieldRow`).
 */
const FIELD_FORMATS: ReadonlySet<string> = new Set(['all-play', 'points-total', 'elimination']);

/** True when the format's weeks are played against the field, not an opponent. */
export function playsTheField(formatId: string): boolean {
  return FIELD_FORMATS.has(formatId);
}

/**
 * True when the format's table is a race on points with no record: total
 * points, and elimination (B-361). A client shows points and points back
 * where another format shows a record.
 */
export function racesOnPoints(formatId: string): boolean {
  return formatId === 'points-total' || formatId === 'elimination';
}

/**
 * True when the rules play a game against the league median each week: a
 * head-to-head league with the switch on. A league against the field has no
 * second game to add, whatever the switch says.
 */
export function playsTheMedian(settings: Pick<LeagueSettings, 'format'>): boolean {
  return settings.format.h2h.medianGame && !playsTheField(settings.format.id);
}

/**
 * True when every regular-season week is two games, the team's one score
 * against each of two opponents (scenario Q6, Yahoo's Second Opponent,
 * PLAN-2026-11-01-two-opponents-a-week): a head-to-head league with the
 * switch on. A league against the field has nobody to play twice.
 */
export function playsTwoOpponents(settings: Pick<LeagueSettings, 'format'>): boolean {
  return settings.format.h2h.twoOpponents && !playsTheField(settings.format.id);
}

/**
 * The week's median: the middle score, the average of the two middle ones
 * when the count is even (Yahoo's rule, PLAN §2). Null with no scores.
 * Rounded as the scorer rounds a score, so a stored median and a recomputed
 * one are the same number.
 */
export function weekMedian(scores: readonly number[]): number | null {
  if (scores.length === 0) return null;
  const sorted = [...scores].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const value = sorted.length % 2 === 1 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
  return Math.round(value * 1000) / 1000;
}

/** Where a score stands against the median: a win above it, a loss below, a tie on it. */
export function againstMedian(score: number, median: number): 'above' | 'below' | 'level' {
  return score > median ? 'above' : score < median ? 'below' : 'level';
}

/**
 * A median game's id, derived as `matchupId` is: one per team per week, so a
 * second scoring pass names the same row. The suffix keeps it apart from the
 * team's head-to-head game, whose id is the league, the week and the home team.
 */
export function medianMatchupId(leagueId: string, period: number, teamId: string): string {
  return `${leagueId}|${period}|${teamId}|median`;
}

/** The format as every view carries it (`LeagueRef.format`). */
export interface FormatRef {
  id: string;
  medianGame: boolean;
  /** Two games a regular-season week (`playsTwoOpponents`, Q6). */
  twoOpponents: boolean;
  bestBall: boolean;
  knockoutsPerPeriod: number | null;
}

/** A plain head-to-head league's: what a league with no readable rules plays. */
export const HEAD_TO_HEAD: FormatRef = {
  id: 'h2h-points',
  medianGame: false,
  twoOpponents: false,
  bestBall: false,
  knockoutsPerPeriod: null,
};

/**
 * The format from a stored settings blob, read for the four things the ref
 * needs. A blob that will not parse plays head-to-head on the ref, as the
 * reads' `peek` falls back: a view is still worth drawing when the rules
 * cannot be read, and the operations that need the rules refuse on them.
 */
export function formatRefOfStored(json: string | null): FormatRef {
  if (json === null) return HEAD_TO_HEAD;
  try {
    const s = JSON.parse(json) as {
      format?: {
        id?: string;
        h2h?: { medianGame?: boolean; twoOpponents?: boolean };
        elimination?: { eliminatePerPeriod?: number } | null;
      };
      lineup?: { bestBall?: boolean };
    };
    const id = s.format?.id ?? HEAD_TO_HEAD.id;
    return {
      id,
      medianGame: (s.format?.h2h?.medianGame ?? false) && !playsTheField(id),
      twoOpponents: (s.format?.h2h?.twoOpponents ?? false) && !playsTheField(id),
      bestBall: s.lineup?.bestBall ?? false,
      knockoutsPerPeriod:
        id === 'elimination' ? (s.format?.elimination?.eliminatePerPeriod ?? 1) : null,
    };
  } catch {
    return HEAD_TO_HEAD;
  }
}

export function formatRefOf(settings: Pick<LeagueSettings, 'format' | 'lineup'>): FormatRef {
  return {
    id: settings.format.id,
    medianGame: playsTheMedian(settings),
    twoOpponents: playsTwoOpponents(settings),
    bestBall: settings.lineup.bestBall,
    knockoutsPerPeriod:
      settings.format.id === 'elimination'
        ? (settings.format.elimination?.eliminatePerPeriod ?? 1)
        : null,
  };
}

/**
 * A team's record over its games of one week, once every one of them is
 * final: in a week of two (Q6) 2–0, 1–1 or 0–2, a tie counted as one. Null
 * while any is still to be decided, and with none. By the result each game
 * recorded (`winnerTeamId`), never by its scores.
 */
export function weekRecordOf(
  games: readonly {
    status: string;
    winnerTeamId: string | null;
    home: { team: { id: string } };
    away: { team: { id: string } };
  }[],
  teamId: string,
): { wins: number; losses: number; ties: number } | null {
  const mine = games.filter((g) => g.home.team.id === teamId || g.away.team.id === teamId);
  if (mine.length === 0 || mine.some((g) => g.status !== 'final')) return null;
  const wins = mine.filter((g) => g.winnerTeamId === teamId).length;
  const ties = mine.filter((g) => g.winnerTeamId === null).length;
  return { wins, losses: mine.length - wins - ties, ties };
}
